import React, { useMemo, useState, useEffect } from 'react'
import classNames from 'classnames'
import { Centre } from '../../../../types/centre'
import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'
import { toggleScroll } from '../.'
import type {
  Item,
  WithNavigationMenu,
  ParentItem,
  InitialPathType,
} from '../types'

import style from './index.module.scss'
import { CentreSelector } from './centre-selector'
import { Nav } from './nav'

const lookupPath = (items: Item[] | undefined, path: string[]): number[] => {
  if (!items || !path.length) {
    return []
  }
  const [key, ...rest] = path
  const i = items.findIndex(item => (item as ParentItem)?.key === key)
  if (i === -1) {
    return []
  }
  const item = items[i] as ParentItem
  return [i, ...lookupPath(item.items, rest)]
}

export const HamburgerMenu: React.FC<{
  country: string
  centres?: Centre[]
  centre?: Centre & WithNavigationMenu
  shop?: WithNavigationMenu
  giftCards?: Item[]
  initialPath?: InitialPathType
  onClose: () => void
  menuId: string
}> = ({
  country,
  centres,
  centre,
  shop,
  giftCards,
  initialPath,
  onClose,
  menuId,
}) => {
  useEffect(() => () => toggleScroll(false), [])
  const findACentreNodes = useMemo(() => {
    if (!centres) {
      return undefined
    }

    const centresByRegion = centres.reduce<{ [key: string]: Array<Centre> }>(
      (acc, c) => {
        let arr = acc[c.state]
        if (!arr) {
          arr = []
          acc[c.state] = arr
        }
        arr.push(c)
        return acc
      },
      {}
    )

    return [
      () => (
        <CentreSelector centres={centres} centre={centre} onClose={onClose} />
      ),
      () => (
        <div className={classNames(style.subtitle, style.regions)}>Regions</div>
      ),
      ...(country === 'New Zealand'
        ? [
            ['AUCKLAND', 'Auckland'],
            ['CHRISTCHURCH', 'Christchurch'],
          ]
        : [
            ['ACT', 'Australian Capital Territory'],
            ['NSW', 'New South Wales'],
            ['QLD', 'Queensland'],
            ['SA', 'South Australia'],
            ['VIC', 'Victoria'],
            ['WA', 'Western Australia'],
          ]
      )
        .filter(([key]) => centresByRegion[key])
        .map(([key, title]) => ({
          title,
          items: centresByRegion[key]
            .sort((a, b) => a.title.localeCompare(b.title))
            .map(c => ({
              label: c.title,
              url: `/${c.slug}`,
              onClick: onClose,
            })),
        })),
    ]
  }, [country, centres, centre])

  const items = useMemo(
    () => [
      shop?.navigationMenu?.length
        ? {
            key: 'shop',
            title: 'Shop online',
            items: shop.navigationMenu,
            className: classNames(style['item-big']),
          }
        : undefined,
      centre
        ? {
            key: 'centre',
            title: centre.title,
            hideTitle: true,
            items: [
              () => (
                <div className={classNames(style.item)}>
                  <div className={classNames(style.title)}>{centre.title}</div>
                  <button
                    className={classNames(style.link)}
                    onClick={() => {
                      setPath(lookupPath(items, ['find-a-centre']))
                      trackEvent(
                        analyticsUtils.categories.UNIVERSAL_HEADER,
                        analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_OPEN,
                        `Edit centre`
                      )
                    }}
                  >
                    Change
                  </button>
                </div>
              ),
              {
                key: 'Home',
                label: 'Home',
                url: `/${centre.slug}`,
              },
              ...centre.navigationMenu,
            ],
            className: classNames(style['item-big']),
          }
        : findACentreNodes && {
            key: 'find-a-centre',
            title: 'Find a centre',
            items: findACentreNodes,
            className: classNames(style['item-big']),
          },
      giftCards && {
        key: 'gift-cards',
        title: 'Gift cards',
        items: giftCards,
        className: classNames(style['item-big']),
      },
      {
        key: 'the-edit',
        label: 'The Edit',
        url: '/the-edit',
        className: classNames(style['item-big']),
      },
      centre &&
        findACentreNodes && {
          key: 'find-a-centre',
          title: 'Find a centre',
          items: findACentreNodes,
        },
      {
        key: 'westfield-plus',
        label: 'Westfield Plus',
        url: '/westfield-plus',
        attrs: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      {
        key: 'support',
        label: 'Customer Support',
        url:
          country === 'New Zealand'
            ? 'https://support.westfield.co.nz'
            : 'https://support.westfield.com.au',
      },
    ],
    [country, centre, shop, giftCards, findACentreNodes]
  )

  const [path, setPath] = useState(() =>
    initialPath
      ? lookupPath(items, initialPath.filter(Boolean) as string[])
      : []
  )

  return (
    <Nav menuId={menuId} items={items} initialPath={path} onClose={onClose} />
  )
}
